import React, { useState, useEffect, useMemo, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Card, Tabs, Tab, Accordion } from '@theneatcompany/nui'
import { FormattedDate } from 'react-intl'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import isPlainObject from 'lodash/isPlainObject'
import sortBy from 'lodash/sortBy'
import cx from 'classnames'

import Column from '@/components/layout/Column'
import Row from '@/components/layout/Row'
import Section from '@/components/layout/Section'
import Layout from '@/components/layout/Layout'
import FeaturesFooter from '@/components/sections/FeaturesFooter'
import useBreakpoint from '@/hooks/useBreakpoint'
import { MOBILE_BREAKPOINT } from '@/constants'
import { NEAT_DATA_URL } from '@/config'
import usePrevious from '@/hooks/usePrevious'
import { Button, Text } from '@/components/common'

import styles from './product-releases.module.scss'

const pageId = 'productReleases'
const baseUrl = `${NEAT_DATA_URL}/releases`

const fetchReleases = () => fetch(`${baseUrl}/dates.json`)
  .then(response => response.json())
  .then(dates => dates.reverse())

const fetchRelease = (date) => fetch(`${baseUrl}/pages/${date}.html`)
  .then(response => response.text())
  .then(text => text.replaceAll('{{BASE_URL}}', baseUrl))

function Post({ date }) {
  const { data } = useQuery({
    queryKey: ['releases', date],
    queryFn: () => fetchRelease(date),
    staleTime: Infinity,
    placeholderData: 'Loading...',
  })

  return <div className={styles.post} dangerouslySetInnerHTML={{ __html: data }} />
}

Post.propTypes = {
  date: PropTypes.string.isRequired,
}

const monthsSortOrder = {
  January: 12,
  February: 11,
  March: 10,
  April: 9,
  May: 8,
  June: 7,
  July: 6,
  August: 5,
  September: 4,
  October: 3,
  November: 2,
  December: 1,
}

const sortYearKeys = years => years.sort((a, b) => b - a)
const sortMonthKeys = months => sortBy(months, m => monthsSortOrder[m])

export default function ProductReleases() {
  const isDesktop = useBreakpoint(MOBILE_BREAKPOINT)
  const queryClient = useQueryClient()

  const { data: dates } = useQuery({
    queryKey: ['releases'],
    queryFn: () => fetchReleases(),
    staleTime: Infinity,
    placeholderData: [],
  })

  const formattedData = useMemo(() => {
    if (!Array.isArray(dates)) return {}
    return dates.reduce((acc, cur) => {
      if (typeof cur !== 'string') return acc
      if (cur.length === 4) {
        acc[cur] = cur
        return acc
      }
      const curAsDate = new Date(`${cur}T00:00:00`)
      const year = curAsDate.getFullYear()

      if (!acc[year]) {
        acc[year] = {}
      }
      const month = format(curAsDate, 'LLLL')
      acc[year][month] = Array.isArray(acc[year][month]) ? [...acc[year][month], cur] : [cur]
      return acc
    }, {})
  }, [dates])

  const [selectedTabId, setSelectedTabId] = useState(dates[0])
  const [expandedYear, setExpandedYear] = useState(sortYearKeys(Object.keys(formattedData))[0])

  // not setting the initial expanded month right away to avoid an issue where the children
  // of the month accordion were not all being rendered (see usage of timeout below when setting
  // expandedMonth after the expandedYear changes)
  const [expandedMonth, setExpandedMonth] = useState(null)

  const prevExpandedYear = usePrevious(expandedYear)
  const prevExpandedMonth = usePrevious(expandedMonth)
  const prevSelectedTabId = usePrevious(selectedTabId)

  useEffect(() => {
    setExpandedYear(sortYearKeys(Object.keys(formattedData))[0])
  }, [formattedData])

  useEffect(() => {
    if (expandedYear !== prevExpandedYear) {
      if (expandedYear === '2019') {
        setExpandedMonth(null)
        setSelectedTabId('2019')
      } else {
        const yearOrMonths = formattedData[expandedYear]
        const firstMonth = isPlainObject(yearOrMonths) ? sortMonthKeys(Object.keys(yearOrMonths))[0] : null
        setTimeout(() => {
          setExpandedMonth(firstMonth)
        }, 200)
      }
    }
  }, [expandedYear, formattedData])

  useEffect(() => {
    if (selectedTabId && selectedTabId !== prevSelectedTabId) {
      let element = document.documentElement
      if (!isDesktop) {
        const elId = selectedTabId.length === 4 ? `year-only-button-${selectedTabId}` : `nui-tab-title_releases_${selectedTabId}`
        element = document.getElementById(elId)
      }
      setTimeout(() => {
        element?.scrollIntoView({ behavior: 'smooth' })
      }, 200)
    }
  }, [selectedTabId, isDesktop])

  const prefetchRelease = (date) => queryClient.prefetchQuery({
    queryKey: ['releases', date],
    queryFn: () => fetchRelease(date),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (expandedMonth && expandedMonth !== prevExpandedMonth) {
      setSelectedTabId(formattedData[expandedYear]?.[expandedMonth]?.[0])
    }
  }, [formattedData, expandedMonth, expandedYear])

  const renderDateTabs = (datesForMonth) => {
    const renderTabs = () => datesForMonth.reduce((acc, date) => {
      const tabPanel = (
        <Post key={date} date={date} />
      )
      const tab = (
        <Tab
          key={date}
          id={date}
          onMouseOver={() => prefetchRelease(date)}
          title={date.length === 4 ? date : <FormattedDate day="numeric" month="short" value={`${date}T00:00:00`} year="numeric" />}
        />
      )

      acc.push(tab)
      if (!isDesktop && selectedTabId === date) acc.push(tabPanel)

      return acc
    }, [])
    return (
      <Tabs className={styles.tabs} id="releases" onChange={setSelectedTabId} selectedTabId={selectedTabId} vertical>
        {renderTabs()}
      </Tabs>
    )
  }

  const renderYearOnlyButton = (year) => (
    <Fragment key={year}>
      <Button className={styles.yearOnlyButton} id={`year-only-button-${year}`} onClick={() => setExpandedYear(year)} size="large" variant="default">
        <Text bold brandPrimary={selectedTabId === year} size="large">{year}</Text>
      </Button>
      {!isDesktop && selectedTabId === year && (
        <Post date={year} />
      )}
    </Fragment>

  )

  const renderAccordions = () => sortYearKeys(Object.keys(formattedData)).map((year) => {
    const isExpanded = expandedYear === year
    const yearOrMonths = formattedData[year]
    const months = isPlainObject(yearOrMonths) ? Object.keys(yearOrMonths) : null

    const closedYearHasSelected = !isExpanded && (year === '2019' ? year === selectedTabId : selectedTabId?.split('-')[0] === year)
    return months ? (
      <Accordion
        key={year}
        iconProps={{ ...closedYearHasSelected ? { fill: 'primary' } : {} }}
        isExpanded={isExpanded}
        label={year}
        labelProps={{ ...closedYearHasSelected ? { color: 'tint-brand-primary' } : {} }}
        onClick={() => {
          setExpandedMonth(null)
          setExpandedYear(isExpanded ? null : year)
        }}
        size="large"
      >
        {months.map((month) => {
          const isExp = expandedMonth === month
          const hasClosedSelected = !isExp && yearOrMonths[month]?.includes(selectedTabId)
          return (
            <Accordion
              key={month}
              className={styles.monthAccordion}
              iconProps={{ ...hasClosedSelected ? { fill: 'primary' } : {} }}
              isExpanded={isExp}
              label={month}
              labelProps={{ ...hasClosedSelected ? { color: 'tint-brand-primary' } : {} }}
              onClick={() => setExpandedMonth(isExp ? null : month)}
            >
              {renderDateTabs(yearOrMonths[month])}
            </Accordion>
          )
        })}
      </Accordion>
    ) : renderYearOnlyButton(year)
  })

  return (
    <Layout.Content>
      <Section bgBlob>
        <Row>
          <Column>
            <h3>We're always looking to create the best experience possible within our small business bookkeeping tools.</h3>
            <p>This page is where you can find all of our latest product updates for the Neat web and mobile applications. We’re always looking for ways to make Neat better, and we’d love to hear from you! If you have an idea, suggestion, or feedback for an upcoming release, share it with us directly from within the Neat web or mobile apps. Visit our <a href="https://support.neat.com/customerservice/feedback-form" rel="noopener noreferrer" target="_blank">Submit Feedback support article</a> to learn how.</p>
          </Column>
        </Row>
      </Section>
      <Section paddingTop={18}>
        <Card className={styles.card}>
          <div className={cx(styles.wrapper)}>
            <div className={styles.accordions}>
              {renderAccordions()}
            </div>
            {isDesktop && (
              <div className={styles.postWrapper}>
                {selectedTabId && <Post date={selectedTabId} />}
              </div>)}
          </div>

        </Card>
      </Section>
      <FeaturesFooter />
    </Layout.Content>
  )
}

ProductReleases.layoutProps = {
  pageId,
}
